import { Link } from "react-router-dom"
import styles from "./StudentTestResult.module.css"
import { Each } from "../common/Each"
import Card from "../components/cards/Card"
import { formatSeconds, formatDateV2, formatTimeV2, toLetter } from "../utils"
import { useTranslation } from "react-i18next"
import { ReactComponent as CrossIcon } from "../assets/images/icons/ic-close.svg"
import { ReactComponent as CheckIcon } from "../assets/images/icons/ic-check.svg"
import UserPlaceholder from "../assets/images/placeholders/user-placeholder.png"
import { useMemo } from "react"
import ResultsDonut from "../components/ResultsDonut"
import StatusBadge from "../components/StatusBadge"
import { TestType } from "../enums"
import TrueFalseBadge from "../components/tests/TrueFalseBadge"
import ReactMarkdown from "react-markdown"

const StudentTestResult = ({ student, test, successThreshold }) => {

  const { t } = useTranslation()

  const circularData = useMemo(() => {

    let correct = 0
    let wrong = 0
    let notAnswered = 0
    for (const innerTest of test) {
      const { testType } = innerTest
      switch (testType) {
        case TestType.SingleChoice:
        case TestType.MultipleChoice: {
          let c = 0
          let w = 0
          let givenResponse = 0
          for (const answer of innerTest.answers) {
            const isStudentAnswer = answer.students.map((s) => s.id).includes(student.id)
            if (answer.isCorrect && isStudentAnswer) {
              c += 1
              givenResponse += 1
            } else if (!answer.isCorrect && isStudentAnswer) {
              w += 1
              givenResponse += 1
            }
          }
          correct += c
          wrong += w
          const nCorrect = innerTest.answers.filter(a => a.isCorrect).map(a => a.id).length
          if (nCorrect > givenResponse) {
            notAnswered += nCorrect - givenResponse
          }
        } break;

        case TestType.TrueFalse: {
          const { answers } = innerTest
          for (const answer of answers) {
            const { usersAnswers } = answer
            const userAnswer = usersAnswers.find(ua => ua.user.id === student.id)
            if (userAnswer?.isCorrect) {
              correct += 1
            } else if (!userAnswer?.isCorrect && userAnswer?.value != null) {
              wrong += 1
            } else {
              notAnswered += 1
            }
          }
        } break
        case TestType.TextCompletion: {
          const { words } = innerTest
          const unknowns = words.filter(w => w.hidden)
          for (const word of unknowns) {
            const { usersAnswers } = word
            const userAnswer = usersAnswers.find(ua => ua.user.id === student.id)
            if (userAnswer && userAnswer.isCorrect) {
              correct += 1
            } else if (userAnswer && userAnswer.isCorrect === false && userAnswer.answer !== "" && userAnswer.answer !== null) {
              wrong += 1
            } else {
              notAnswered += 1
            }
          }
        } break
        default: console.error("Unsupported test type"); break
      }
    }

    const sum = correct + wrong + notAnswered

    const pc = Math.round(((correct) / (sum || 1) * 100) * 100) / 100
    const pw = Math.round(((wrong) / (sum || 1) * 100) * 100) / 100
    const pna = Math.round(((notAnswered) / (sum || 1) * 100) * 100) / 100

    return [
      {
        id: "correct",
        value: pc,
        rawValue: correct,
        color: 'var(--sf-light-green)',
        label: t("tests.correctAnswers")
      },
      {
        id: "wrong",
        value: pw,
        rawValue: wrong,
        color: 'var(--sf-red)',
        label: t("tests.wrongAnswers")
      },
      {
        id: "notAnswered",
        value: pna,
        rawValue: notAnswered,
        color: '#B5B8BE',
        label: t("tests.notAnswered")
      },
    ]
  }, [test, student.id, t])

  return (
    <div className={styles.container}>


      <div className={styles.top}>
        <Card appearance="modern" title="Informazioni studente" actions={student.id && <Link className={styles.profileLink} to={`/users/students/${student.id}`}>Apri profilo</Link>}>
          <div className={styles.studentIdentity}>
            <img className={styles.picture} src={student.picture || UserPlaceholder} alt="" />
            <div><h1 className={styles.name}>{student.name} {student.surname}</h1>{student.email && <p className={styles.email}>{student.email}</p>}</div>
          </div>
          <dl className={styles.studentInfo}>
            <div><dt>Esito</dt><dd><StatusBadge appearance="soft" label={student.statusLabel || "—"} color={student.resultStatus === "passed" ? "var(--primary)" : student.resultStatus === "failed" ? "var(--sf-red)" : "#64748b"} /></dd></div>
            <div><dt>Durata</dt><dd>{student.duration != null ? formatSeconds(student.duration) : "—"}</dd></div>
            <div><dt>Inizio</dt><dd>{student.started_at ? `${formatDateV2(student.started_at)} · ${formatTimeV2(student.started_at)}` : "—"}</dd></div>
            <div><dt>Ultimo aggiornamento</dt><dd>{student.finished_at ? `${formatDateV2(student.finished_at)} · ${formatTimeV2(student.finished_at)}` : "—"}</dd></div>
            <div><dt>Soglia di superamento</dt><dd>{successThreshold ?? 0} risposte corrette</dd></div>
          </dl>
        </Card>
        <Card appearance="modern" title="Riepilogo risposte" style={{ flexGrow: 1 }}>
          <div className={styles.recap}>

            <div className={styles.circularChartColumn}>
              <ResultsDonut data={circularData} label="Risposte" />
              <div className={styles.chartLabels}>
                {circularData.map(data => {
                  return (
                    <div className={styles.chartCell} key={data.label}>
                      <div className={styles.chartRow}>
                        <div className={styles.chartColumn}>
                          <div className={styles.chartColor} style={{ backgroundColor: data.color }}></div>
                        </div>
                        <div className={styles.chartColumn}>
                          <div className={styles.chartDataTitle}>{data.rawValue}</div>
                          <div className={styles.chartDataSubitle} style={{ color: data.color }}>{data.label}</div>
                        </div>
                      </div>
                    </div>
                  )
                })}
                <div className={styles.chartCell}>
                  <div className={styles.chartRow}>
                    <div className={styles.chartColumn}>
                      <div className={styles.chartColor} style={{ backgroundColor: 'var(--secondary)' }}></div>
                    </div>
                    <div className={styles.chartColumn}>
                      <div className={styles.chartDataTitle}>{formatSeconds(student.duration)}</div>
                      <div className={styles.chartDataSubitle} style={{ color: 'var(--secondary)' }}>Tempo</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      <div className={styles.content}>
        <Each
          of={test}
          render={(innerTest) => (
            <Card appearance="modern" style={{ flexGrow: 1, padding: 0 }}>
              <div className={styles.innerTest}>

                {
                  innerTest.text &&
                  <div className={styles.cardTitle} style={{
                    padding: "0 1rem",
                    paddingTop: "1rem",
                  }}>{innerTest.text}</div>
                }
                {
                  (innerTest.testType === TestType.SingleChoice || innerTest.testType === TestType.MultipleChoice) &&
                  <Each
                    of={innerTest.answers}
                    render={(answer, index) => {
                      const isStudentAnswer = answer.students.map((s) => s.id).includes(student.id)
                      let resultBadge = null
                      let answerClass = answer.isCorrect ? `${styles.answer} ${styles.correct}` : styles.answer
                      if (isStudentAnswer && answer.isCorrect) {
                        resultBadge = {
                          label: t("tests.correct"),
                          Icon: CheckIcon,
                          class: `${styles.badge} ${styles.correct}`
                        }

                      } else if (isStudentAnswer && !answer.isCorrect) {
                        answerClass = `${styles.answer} ${styles.wrong}`
                        resultBadge = {
                          label: t("tests.wrong"),
                          Icon: CrossIcon,
                          class: `${styles.badge} ${styles.wrong}`
                        }
                      }

                      return (
                        <div className={styles.answerContainer}>
                          <div className={styles.answerWrapper}>
                            <div className={answerClass}>
                              <div className={styles.left}>
                                <div className={answer.isCorrect ? styles.correctLabel : styles.wrongLabel}>{toLetter(index + 1).toUpperCase()}</div>
                                <ReactMarkdown children={answer.text} />
                              </div>
                              {
                                resultBadge !== null &&
                                <div className={resultBadge.class}>{resultBadge.label} <resultBadge.Icon style={{ width: "16px", height: "16px" }} /></div>
                              }
                            </div>

                          </div>
                        </div>
                      )
                    }}
                  />
                }
                {
                  innerTest.testType === TestType.TrueFalse &&
                  <Each
                    of={innerTest.answers}
                    render={(answer) => (
                      <div className={styles.answerContainer} >
                        <div className={styles.answerWrapper}>
                          <div className={answer.usersAnswers.find(ua => ua.user.id === student.id)?.isCorrect ? styles.answer : `${styles.answer} ${styles.wrong}`}>
                            <ReactMarkdown children={answer.text} />
                            <div className={styles.badges}>
                              <TrueFalseBadge type={true} onChange={null} value={answer.value} />
                              <TrueFalseBadge type={false} onChange={null} value={answer.value} />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  />
                }
                {
                  innerTest.testType === TestType.TextCompletion &&
                  <div className={styles.textCompletion}>
                    <div className={styles.words}>
                      <Each
                        of={innerTest.words}
                        render={(word) => {
                          if (!word.hidden) {
                            return <div className={styles.word}>{word.text}</div>
                          } else {
                            const userAnswer = word.usersAnswers.find(ua => ua.user.id === student.id)
                            if (word.hidden && !userAnswer.isCorrect) {
                              return <div className={`${styles.hiddenWord} ${styles.wrong}`}><span>{userAnswer.answer}</span> <span>{word.solutions[0]}</span></div>
                            } else if (word.hidden && userAnswer.isCorrect) {
                              return <div className={styles.hiddenWord}>{userAnswer.answer}</div>
                            }
                          }
                        }}
                      />
                    </div>
                    <div className={styles.solutions}>
                      <Each
                        of={innerTest.words.filter(w => w.hidden)}
                        render={(hiddenWord, index) => (
                          <div className={styles.solutionContainer}>
                            <div>{`${index + 1}. `}{hiddenWord.usersAnswers.find(ua => ua.user.id === student.id).answer}</div>
                            {hiddenWord.usersAnswers.find(ua => ua.user.id === student.id).answer && "->"}
                            <div className={styles.solution}>{hiddenWord.solutions.join(", ")}</div>
                          </div>
                        )}
                      />
                    </div>
                  </div>
                }
              </div>
            </Card>

          )}
        />
      </div>
    </div >
  )

}

export default StudentTestResult
